import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PutMeCategoryResult,
  PutMeCategoryOptions,
  DeleteMeCategoryResult,
  DeleteMeCategoryOptions,
  GetMeCategoryOptions,
  GetMeCategoryResult,
  getMeCategory,
  PutMeCategoryFields,
  DeleteMeCategoryFields,
  GetMeCategoryFields,
  putMeCategory,
  deleteMeCategory,
} from '../fetchers/me/category';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetMeCategoryOptions<Fields extends GetMeCategoryFields> = GetMeCategoryOptions<Fields>;

/**
 * Check if the user follows a category
 * This method determines whether the authenticated user follows the specified category.
 */

export function useGetMeCategory<Fields extends GetMeCategoryFields>(
  getOptions: UseGetMeCategoryOptions<Fields> | (() => UseGetMeCategoryOptions<Fields> | null),
  config?: SWRConfiguration<GetMeCategoryResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/me/categories/${options.where.category}${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getMeCategory({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeCategory, {
    endpoint: '/me/categories/:category',
    method: 'GET',
  });
}

/**
 * Check if the user follows a category
 * This method determines whether the authenticated user follows the specified category.
 */

export function useGetMeCategoryLazy<Fields extends GetMeCategoryFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetMeCategoryResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetMeCategoryOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/categories/${options.where.category}${serializeQuery(options)}`,
          getMeCategory({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeCategoryLazy, {
    endpoint: '/me/categories/:category',
    method: 'GET',
  });
}

type UsePutMeCategoryOptions<Fields extends PutMeCategoryFields> = PutMeCategoryOptions<Fields>;

/**
 * Cause the user to follow a specific category
 * This method causes the authenticated user to follow the specified category.
 */

export function usePutMeCategory<Fields extends PutMeCategoryFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PutMeCategoryResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePutMeCategoryOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/categories/${options.where.category}${serializeQuery(options)}`,
          putMeCategory({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePutMeCategory, {
    endpoint: '/me/categories/:category',
    method: 'PUT',
  });
}

type UseDeleteMeCategoryOptions<Fields extends DeleteMeCategoryFields> =
  DeleteMeCategoryOptions<Fields>;

/**
 * Cause the user to stop following a category
 * This method causes the authenticated user to stop following the specified category.
 */

export function useDeleteMeCategory<Fields extends DeleteMeCategoryFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteMeCategoryResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteMeCategoryOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/categories/${options.where.category}${serializeQuery(options)}`,
          deleteMeCategory({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteMeCategory, {
    endpoint: '/me/categories/:category',
    method: 'DELETE',
  });
}
